<template>
  <section class="main">
    <loader v-if="!configLoaded"/>
    <h1 v-if="configLoaded && isDeviceLinked" class="intro">
      <span class="intro__inner">{{ $t('PLUS_STATUS_FOR_DEVICE') }}</span>
      <span
        class="intro__status g-status"
        :class="{
          'g-status_success': hasPlus,
        }"
      >
        {{ $t(status) }}
      </span>
    </h1>
    <div v-if="configLoaded" class="row">
      <div class="col col_1">
        <sideloading-options v-if="!hasPlus" :options="plusOptions" hide-name class="buy" />

        <template v-if="isDeviceLinked">

          <table
            class="table"
          >
            <template
              v-if="!config.plus_account?.appleid
                || config.plus_account?.is_yours === 0
                || configuring
                || showConfigure"
              >
              <tr>
                <td class="cell">
                  Apple ID {{ $t('OF_YOUR_OWN_DEV_ACCOUNT') }}<br/>
                  <a href="https://www.youtube.com/watch?v=-ShhaoaLWyE" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
                </td>
                <td class="td">
                  <input class="input" v-model="appleid"/>
                </td>
              </tr>
              <tr>
                <td class="cell">
                  {{ $t('PASSWORD') }}
                </td>
                <td class="td">
                  <input class="input" type="password" v-model="password"/>
                </td>
              </tr>
              <tr>
                <td class="cell">
                </td>
                <td class="td">
                  <html-with-links
                    tag="span"
                    class="hint"
                    :html="$t('PLUS_PRIVACY_HINT', {privacy_href: '/info/privacy'})"
                  />
                </td>
              </tr>
              <tr>
                <td class="cell"></td>
                <td class="td">
                  <btn
                    class="configure-button"
                    type="primary"
                    :loading="configuring"
                    :disabled="!appleid || !password"
                    @click.native="configurePlusAccount"
                  >
                    {{ $t('CONFIGURE_PLUS_ACCOUNT') }}
                  </btn>
                </td>
              </tr>
            </template>
            <tr v-if="config.plus_account?.appleid && config.plus_account?.is_yours !== 0">
              <td class="cell">
                Apple ID<br/>
                <a href="https://www.youtube.com/watch?v=-ShhaoaLWyE" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
              </td>
              <td class="td">
                {{ config.plus_account?.appleid }}
                <template v-if="!showConfigure">
                  <br/>
                  <btn
                    class="configure-button"
                    type="primary"
                    @click.native="showConfigure = true;"
                  >
                    {{ $t('RECONFIGURE_YOUR_ACCOUNT') }}
                  </btn>
                </template>
              </td>
            </tr>
            <tr v-if="!config.plus_account?.shared_by && config.plus_account?.appleid">
              <td class="cell"></td>
              <td class="td">
                <btn
                  class="delete-account-button"
                  type="negative"
                  :loading="deletingAccount"
                  @click.native="deleteAccount"
                >
                  {{ $t('DELETE_PLUS_DEV_ACCOUNT') }}
                </btn>
              </td>
            </tr>
            <tr v-if="config.plus_account?.shared_by">
              <td class="cell">
                {{ $t('SHARED_WITH_YOU_BY') }}
              </td>
              <td class=td>{{ config.plus_account?.shared_by }}</td>
            </tr>
            <tr v-if="config.plus_account?.status_translated">
              <td class="cell">
                {{ $t('STATUS') }}
              </td>
              <td class="td">
                <span v-html="config.plus_account?.status_translated"></span>
                <template v-if="config.plus_account?.status === 'otp_required' && !configuring">
                  <br/>
                  <btn
                    class="configure-button"
                    type="primary"
                    :loading="sendingOtp"
                    @click.native="sendOtp"
                  >
                    {{ $t('ENTER_OTP_SHORT') }}
                  </btn>
                </template>
                <template v-if="config.plus_account?.status === 'ok'
                  && !config.plus_account?.shared_by">
                  <br/>
                  <btn
                    class="configure-button"
                    type="primary"
                    @click.native="downloadArchive"
                  >
                    {{ $t('DOWNLOAD_ARCHIVE_FOR_DEVELOPMENT') }}
                  </btn>
                </template>
              </td>
            </tr>
            <tr v-if="config.plus_account?.appleid && !config.plus_account?.shared_by">
              <td class="cell">
                {{ $t('SHARED_WITH_FRIENDS') }}<br/>
                <a href="https://www.youtube.com/watch?v=v0u7c4z59-s" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>
              </td>
              <td class="td">
                <div
                  class="shared-row"
                  v-for="account in config.plus_account?.shared_with" :key="account.email"
                >
                  {{ account.email }}
                  <span style="white-space: nowrap">
                    ({{ $t('DEVICES_COUNT', { n: account.devices_amount }) }})
                  </span>&nbsp;&nbsp;<btn
                    type="negative"
                    class="unsh-button"
                    :loading="unsharing.includes(account.email)"
                    @click.native="unshare(account.email)"
                  >{{ $t('Remove') }}</btn>
                </div>
                <btn
                  type="primary"
                  class="sh-action"
                  @click.native="share"
                  :loading="sharing"
                >{{ $t('SHARE_WITH_FRIEND') }}
                </btn>
              </td>
            </tr>
            <tr v-if="config.plus_account && config.plus_account?.is_yours !== 0">
              <td class="cell">
                {{ $t('P2P_TRADING') }}
                <br/>
                <router-link to="/my/p2p-certificates">{{ $t('LEARN_MORE') }}</router-link>
              </td>
              <td class="td">
                <template v-if="config.plus_account?.is_p2p">
                  {{ $t('ENABLED') }} <btn
                    type="primary"
                    @click.native="$router.push('/my/p2p-certificates')"
                    >{{ $t('VIEW_STATUS') }}
                  </btn>
                </template>
                <template v-else>
                  {{ $t('DISABLED') }} <btn
                    type="primary"
                    @click.native="$router.push('/my/p2p-certificates')"
                    >{{ $t('ENABLE') }}
                  </btn>
                </template>
              </td>
            </tr>
            <tr v-if="config.plus_account?.is_yours === 1 && config.plus_account?.is_p2p === 0">
              <td colspan="2">
                <router-link to="/my/p2p-certificates" class="banner">
                  <span class="banner__title">
                    {{ $t('GET_N_CASHBACK', { amount: '100%' }) }}
                  </span>
                  <span class="banner__desc">
                    {{ $t('LEARN_MORE') }}
                  </span>
                </router-link>
              </td>
            </tr>
            <tr v-if="+new Date(config.plus_till * 1000)">
              <td class="cell">
                {{
                  hasActivePlus ?
                    $t('NEXT_BILLING_CYCLE_STARTS_ON') : $t('PLUS_SUBSCRIPTION_ENDS_ON')
                }}
              </td>
              <td class="td">
                {{ new Date(config.plus_till * 1000).toLocaleString() }}
                <template v-if="hasActivePlus">
                  <br/>
                  <btn
                    type="negative"
                    class="cancel-button"
                    @click.native="cancelSubscription"
                  >{{ $t('CANCEL_SUBSCRIPTION') }}
                  </btn>
                </template>
              </td>
            </tr>
          </table>

          <div class="provided" v-if="config.plus_provider && config.plus_support_uri">
            <span class="cell provided__desc">
              {{ $t('PLUS_PROVIDED_BY') }}
            </span>
            <div class="provided__val">
              {{ config.plus_provider }}
              <a
                class="provided__link"
                :href="config.plus_support_uri"
                target="_blank"
              >
                <img class="provided__icon" src="@/assets/icon-support.svg"/>
                {{ $t('GET_SUPPORT') }}
              </a>
              <span class="provided__support">
                {{ $t('SUPPORT_BY_3RD_PARTY') }}
              </span>
            </div>
          </div>

        </template>
      </div>
      <aside class="sidebar col col_2">
        <h2 class="sidebar__heading">{{ $t('PLUS_TERMS_TITLE') }}</h2>
        <p class="sidebar__text g-text" v-html="$t('PLUS_FEATURES_DESC')"></p>
      </aside>
    </div>
  </section>
</template>

<script>
import Loader from '@/components/Loader';
import SideloadingOptions from '@/components/SideloadingOptions.vue';
import Btn from '@/components/Button.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    Loader,
    SideloadingOptions,
    Btn,
    HtmlWithLinks,
  },
  data() {
    return {
      loading: false,
      configuring: false,
      sendingOtp: false,
      showConfigure: false,
      deletingAccount: false,
      sharing: false,
      unsharing: [],
      appleid: '',
      password: '',
      plusOptions: [],
    };
  },
  computed: {
    ...mapGetters(['hasPlus', 'hasActivePlus', 'hasCanceledPlus', 'devicesLoaded', 'isDeviceLinked']),
    title() {
      return this.$t('VIEW_PLUS_STATUS');
    },
    configLoaded() {
      return !!this.config.model || (this.devicesLoaded && !this.isDeviceLinked);
    },
    config() {
      return this.$store.getters.config;
    },
    status() {
      if (this.hasPlus) {
        return 'ACTIVATED';
      }

      return 'NOT_ACTIVATED';
    },
  },
  methods: {
    cancelSubscription() {
      this.$confirm(this.$t('PLUS_SUBSCRIPTION_CANCEL_CONFIRMATION'))
        .then(() => this.$api.cancelPlusSubscription())
        .then((response) => {
          if (response) {
            window.location.href = response;
            return;
          }

          this.$notify({
            type: 'error',
            text: this.$t('SOMETHING_WENT_WRONG'),
          });
        });
    },
    async configurePlusAccount() {
      this.configuring = true;
      const result = await this.$api.editPlusDevAccount({
        appleid: this.appleid,
        password: this.password,
        status: this.config.plus_account?.appleid ? 'new' : '',
        // eslint-disable-next-line camelcase
        sharedWithEmails: this.config.plus_account?.shared_with?.map(a => a.email) || [],
      })
        .catch(e => e);

      if (result instanceof Error) {
        this.configuring = false;
        return;
      }

      this.handleStatus(this.appleid)
        .finally(() => {
          this.configuring = false;
        });
    },
    async handleStatus(appleid) {
      await this.$store.dispatch('getConfig');
      const { status } = this.config.plus_account;

      if (status === 'ok') {
        this.$notify({
          type: 'success',
          text: this.$t('PLUS_ACCOUNT_CONFIGURED'),
        });
        return true;
      }

      if (status === 'error') {
        this.$notify({
          type: 'error',
          text: this.config.plus_account.status_translated,
        });
        return false;
      }

      if (status === 'otp_required') {
        const otp = await this.$prompt(this.$t('ENTER_OTP'));
        await this.$api.editPlusDevAccount({
          appleid,
          otp,
          sharedWithEmails: this.config.plus_account.shared_with.map(a => a.email),
        });
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.handleStatus(appleid));
        }, 1000);
      });
    },
    async unshare(email) {
      this.$confirm(this.$t('DEV_ACCOUNT_SHARE_REMOVAL_CONFIRMATION', { email }))
        .then(() => {
          this.unsharing.push(email);
          return this.$api.editPlusDevAccount({
            appleid: this.config.plus_account.appleid,
            status: 'new',
            sharedWithEmails:
              this.config.plus_account.shared_with.map(a => a.email)
                .filter(e => e !== email),
          });
        })
        .then(() => this.handleStatus(this.config.plus_account.appleid))
        .finally(() => {
          const idx = this.unsharing.indexOf(email);
          this.unsharing.splice(idx, 1);
        });
    },
    share() {
      this.$prompt(this.$t('DEV_ACCOUNT_SHARE_PROMPT'))
        .then((email) => {
          this.sharing = true;
          return this.$api.editPlusDevAccount({
            appleid: this.config.plus_account.appleid,
            status: 'new',
            sharedWithEmails: [...this.config.plus_account.shared_with.map(a => a.email), email],
          });
        })
        .then(() => this.handleStatus(this.config.plus_account.appleid))
        .finally(() => {
          this.sharing = false;
        });
    },
    deleteAccount() {
      this.$confirm(this.$t('DELETE_PLUS_DEV_ACCOUNT_CONFIRMATION'))
        .then(() => {
          this.deletingAccount = true;
          return this.$api.deletePlusDevAccount();
        })
        .then(() => this.$store.dispatch('getConfig'))
        .finally(() => {
          this.deletingAccount = false;
        });
    },
    sendOtp() {
      this.sendingOtp = true;
      this.handleStatus(this.config.plus_account.appleid)
        .finally(() => {
          this.sendingOtp = false;
        });
    },
    downloadArchive() {
      this.$api.getPlusDevAccountArchive()
        .then((response) => {
          if (response) {
            window.location.href = response;
            return;
          }

          this.$notify({
            type: 'error',
            text: this.$t('SOMETHING_WENT_WRONG'),
          });
        });
    },
  },
  mounted() {
    const el = this.$route.hash && document.querySelector(this.$route.hash);
    if (el) {
      el.scrollIntoView();
    }
  },
  created() {
    this.$api.getSideloadingOptions('plus').then((response) => {
      this.plusOptions = response;
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.error-wrapper {
  display: flex;
  margin-bottom: 15px;
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-right: 5px;
  flex-grow: 1;
  font-size: 14px;

  &_mb {
    margin-bottom: 20px;

    @include tablet {
      margin-bottom: 15px;
    }
  }
}

.main {
  display: block;
  flex-grow: 1;
}

.intro {
  &__inner {
    margin-right: 5px;
  }

  &__status {
    font-size: inherit;
    font-weight: 400;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.col {
  &_1 {
    flex-grow: 1;
  }

  &_2 {
    flex: 0 0 192px;
    margin-left: 15px;

    @include notebook {
      margin-left: 0;
      margin-top: 15px;
    }
  }

  @include notebook {
    flex: 0 0 100%;
  }
}

.table {
  line-height: 18px;
  margin-bottom: 15px;

  tr:not(:first-child) td {
    padding-top: 10px;
  }
}

.td {
  padding-left: 40px;
}

.cell {
  width: 140px;
  flex: 0 0 140px;
  word-break: break-word;
}

.provided {
  padding-top: 15px;
  border-top: 1px solid #e5e5e5;
  display: flex;

  &_alone {
    flex-direction: column;

    .provided__support {
      max-width: 100%;
    }

    .provided__link {
      margin-top: 0;
    }
  }

  &__val {
    padding-left: 40px;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__link {
    color: var(--text-primary);
    display: flex;
    align-items: center;
    margin: 7px 0;
  }

  &__icon {
    margin-right: 5px;
  }

  &__support {
    font-size: 12px;
    font-weight: 400;
    max-width: 300px;
  }
}

.button {
  margin-left: 5px;
}

.sidebar {
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;

  &__heading {
    font-weight: 500;
    margin-bottom: 10px;
  }

  &__text {
    &::v-deep {
      p:not(:last-child) {
        margin-bottom: 15px;
      }
    }
  }
}

.list {
  list-style: disc inside;
  margin-bottom: 15px;

  &__item {
    &:not(:last-child) {
      margin-bottom: 5px;
    }
  }
}

.buy {
  margin-bottom: 15px;
}

.buy:not(:first-child) {
  margin-top: 25px;
}

.cancel-button,
.configure-button {
  margin-left: 0;

  &:not(:first-child) {
    margin-top: 5px;
  }
}

.input {
  background: var(--tertiary-bg);
  border: 1px solid var(--tertiary-accent);
  border-radius: 2px;
  padding: 4px 6px;
  margin-left: auto;
  flex: 0 0 150px;
  width: 150px;
  font-size: 14px;
  font-family: "IBM Plex Sans", sans-serif;
  color: var(--text-primary);

  &::placeholder {
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);
  }
}

.hint {
  display: inline-block;
  font-size: 12px;
  font-weight: 400;
  max-width: 300px;
  line-height: 1.2;
}

.warning {
  color: var(--configure-error-text);
  background: var(--bg-error);
  border-radius: 2px;
  padding: 3px 10px;
  text-align: center;
  line-height: 18px;
  margin-bottom: 20px;
}

.shared-row {
  margin-bottom: 5px;
  line-height: 1.6;

  & + .sh-action {
    margin-top: 5px;
  }
}

.unsh-button,
.sh-action,
.delete-account-button {
  margin-left: 0;
}

.banner {
  display: block;
  border-radius: 8px;
  padding: 15px;
  background-image: $my-buy-options-gradient;
  color: #fff;
  font-weight: 500;
  text-align: center;
  cursor: pointer;

  @include tablet {
    padding: 10px;
  }

  &__title {
    display: block;
    font-size: 16px;
    margin-bottom: 5px;

    @include tablet {
      font-size: 14px;
    }
  }

  &__desc {
    display: block;
    font-size: 20px;
    white-space: nowrap;

    @include tablet {
      font-size: 14px;
      font-weight: 700;
    }
  }
}
</style>
